import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik"
import * as Yup from 'yup';
import {
    setAttendeesData,
    mappingAttendeesToTicket,
    updateBookingById,
    setActiveStep,
    getTicketBookingById
} from "../../../../app/store/slices/ticket/ticketBuying";
import { getAllStates } from "../../../../app/store/slices/cities";
import { errorToast } from "../../../../utils/toasts/toasts";

export default function useAttendeeStepper() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        selectedTickets,
        totalTickets,
        ticketTypesWithIds,
        bookingId
    } = useSelector((state) => state.ticketBuying);

    const eventData = useSelector((state) => state.event?.eventData);


    const [states, setStates] = useState([]);
    const [attendees, setAttendees] = useState([])
    const [loading, setLoading] = useState(false);
    const [activeForm, setActiveForm] = useState('attendeeForm');

    const [openModal, setOpenModal] = useState(false);
    const [modalVariant, setModalVariant] = useState("eventDrafted"); // "eventCancelled" | "eventDrafted"

    // Steps of two forms
    const steps = ['Fill Attendee Details', 'Select Billing Details'];


    // Flatten attendees for form initialValues
    const attendeesArray = selectedTickets.flatMap(ticket => {
        const attendeeCount = ticket.isGroupTicket
            ? ticket.quantity * ticket.groupTicketAttendees
            : ticket.quantity;
        return Array.from({ length: attendeeCount }).map(() => ({
            ticketId: ticket.ticketId,
            name: "",
            email: "",
            phone: ""
        }));
    });


    /**
     * Fetch all states for the billing form
     */
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const res = await dispatch(getAllStates()).unwrap();
                if (res.success) {
                    setStates(res.data);
                }
            } catch (error) {
                console.error("Error fetching states:", error);
            }
        };

        if (states.length === 0) {
            fetchStates();
        }
    }, [dispatch, states.length]);


    /**
     * Initial values for the form
     */
    const initialValues = {
        attendees: attendeesArray,
        state: null,
        billing: null
    };


    /**
     * Validation schema for the form
     */
    const validationSchema = Yup.object().shape({
        attendees: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required("Name is required"),
                email: Yup.string().email("Invalid email").required("Email is required"),
                phone: Yup.string()
                    .matches(/^[0-9]{10}$/, "Invalid phone number")
                    .required("Phone number is required")
            })
        ),
        state: Yup.object().nullable(),
        billing: Yup.object().nullable()
    });


    /**
     * Formik configuration
     */
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: () => { }
    });


    /**
     * Handle moving to the next form
     */
    function handleNext() {
        setActiveForm((prevForm) => (prevForm === "attendeeForm" ? "billingForm" : prevForm));
    }


    /**
     * Handle moving to the previous form
     */
    function handleBack() {
        setActiveForm((prevForm) => (prevForm === "billingForm" ? "attendeeForm" : prevForm));
    }


    /**
     * Submit attendee details
     * This function validates and submits the attendee details form.
     * Also mapping booking created sub ids of ticket_type_id in attendee
     */
    // TODO: Convert attendee name in capitalize format
    async function submitAttendeeDetails() {
        try {
            setLoading(true)

            // run validation
            const errors = await formik.validateForm();


            // If there are no error then proceed with logic
            if (Object.keys(errors).length === 0) {

                // Create a copy of ticketTypesWithIds to keep track of used IDs
                const ticketIdsMap = JSON.parse(JSON.stringify(ticketTypesWithIds));


                // Map attendees to actual ticket IDs
                const attendeesData = {
                    tickets: formik.values.attendees.map((attendee) => {
                        const typeId = attendee.ticketId;

                        // Pick the first available ID and remove it from the array
                        // Because by removing we can see how many ids are left and assign them
                        const ticketId = ticketIdsMap[typeId].ids.shift();

                        // Map the attendee details by actual backend ID
                        const attendees = {
                            ticket_id: ticketId,
                            name: attendee.name,
                            phone: attendee.phone,
                            email: attendee.email
                        }

                        // Update local attendees state for billing name options
                        setAttendees(prev => [...prev, attendees])

                        // return all the created attendees
                        return attendees
                    })
                };


                // call api to map attendees with backend given ids
                await dispatch(mappingAttendeesToTicket({
                    attendeesData,
                    passXheader: Boolean(localStorage.getItem("continuedBooking") === "true"),
                    eventId: eventData?.eventId
                })).unwrap()

                setOpenModal(false);
                // Save attendees to Redux
                dispatch(setAttendeesData(formik.values.attendees));


                // move to the next form
                handleNext();
            } else {
                // Mark all attendee fields as touched to show errors
                formik.setTouched(
                    formik.values.attendees.reduce((acc, _, index) => {
                        acc.attendees = acc.attendees || [];
                        acc.attendees[index] = { name: true, email: true, phone: true };
                        return acc;
                    }, {})
                );

                // console.warn("❌ Validation Errors:", errors);
            }
        } catch (error) {

            // Handle specific error scenarios
            if (error?.code === 400) {
                // Check if event is cancelled or drafted

                if (error?.message === 'cancelled') {
                    setModalVariant('eventCancelled');
                    setOpenModal(true);
                } else if (error?.message === 'drafted') {
                    setModalVariant('eventDrafted');
                    setOpenModal(true);
                } else {
                    errorToast(error?.statusText || "Bad Request");
                }

            } else if (error?.code === 404) {
                errorToast(error?.statusText || "Resource not found");
            } else if (error?.code === 500) {
                errorToast(error?.statusText || "Server error occurred");
            } else {
                errorToast(error?.statusText || "An unexpected error occurred.");
            }

        } finally {
            setLoading(false)
        }
    }


    /**
     * Submit billing details
     */
    async function billingDetails() {
        try {
            setLoading(true)
            const bookingData = {
                id: bookingId,
                state_id: formik.values.state?.id,
                invoice_name: formik.values.billing.name,
                invoice_email: formik.values.billing.email,
                invoice_phone: formik.values.billing.phone
            }

            await dispatch(updateBookingById({
                bookingData,
                passXheader: Boolean(localStorage.getItem("continuedBooking") === "true"),
                eventId: eventData?.eventId
            })).unwrap();

            await dispatch(getTicketBookingById(bookingId)).unwrap();
            dispatch(setActiveStep('payment'));
            setOpenModal(false);
        } catch (error) {

            // Handle specific error scenarios
            if (error?.code === 400) {
                // Check if event is cancelled or drafted

                if (error?.message === 'cancelled') {
                    setModalVariant('eventCancelled');
                    setOpenModal(true);
                } else if (error?.message === 'drafted') {
                    setModalVariant('eventDrafted');
                    setOpenModal(true);
                } else {
                    errorToast(error?.statusText || "Bad Request");
                }

            } else if (error?.code === 404) {
                errorToast(error?.statusText || "Resource not found");
            } else if (error?.code === 500) {
                errorToast(error?.statusText || "Server error occurred");
            } else {
                errorToast(error?.statusText || "An unexpected error occurred.");
            }

        } finally {
            setLoading(false)
        }
    }

    function goToEventPage() {
        setOpenModal(false);
        navigate(`/events`);
    }

    function onModalContinue() {

        localStorage.setItem("continuedBooking", "true");

        if (activeForm === 'attendeeForm') {
            submitAttendeeDetails();
        } else {
            billingDetails();
        }
    }


    // return all the needed states and functions
    return {
        steps,
        activeForm,
        handleNext,
        handleBack,
        selectedTickets,
        totalTickets,
        formik,
        submitAttendeeDetails,
        states,
        loading,
        billingDetails,
        attendees,
        openModal,
        setOpenModal,
        modalVariant,
        goToEventPage,
        onModalContinue
    }
}